import * as React from "react";

import "./analytics-layout.scss";
import {Link}                       from "react-router-dom";
import {
    AGENT_LEVEL,
    OFFICE_LEVEL
}                                    from "@Xpresso/routes/common/logic/user-level";
import {ADDRESS_LIST_MESSAGE}        from "./analytics-address-list-modal/analytics-address-list-modal";
import {AnalyticsAddressListModal}   from "./analytics-address-list-modal/analytics-address-list-modal";
import {AnalyticsBlog}               from "./analytics-blog/analytics-blog";
import {AnalyticsFilters}            from "./analytics-filters/analytics-filters";
import {AnalyticsLeadInfo}           from "./analytics-lead-info/analytics-lead-info";
import {AnalyticsLeads, leadColumns} from "./analytics-leads/analytics-leads";
import {AnalyticsLocale}             from "./analytics-locale/analytics-locale";
import {AnalyticsOverview}           from "./analytics-overview/analytics-overview";
import {SelectiveOfficeSelect}       from "./selective-office-select/selective-office-select";
import {SelectiveAgentSelect}        from "./selective-agent-select/selective-agent-select";

const DURATION_NOTIFICATION = 2000
const ID_AGENT_BRANCH_ORDER = 'Branch Order'

export const AnalyticsLayout = ({leadInteractions, idBranch, idAgent, userLevels, isInternalUser, settings, routerHistory}) => {
    const userLevel = userLevels?.userLevel
    const leadsEmpty = (leadInteractions?.loading || !Array.isArray(leadInteractions?.value?.leads) || leadInteractions?.value?.leads?.length < 1)
    const leadCount = leadInteractions?.value?.leads?.length || 0

    const leadArray = leadsEmpty ? [] : leadInteractions?.value?.leads
    const leadInteractionArray = leadsEmpty ? [] : leadInteractions?.value?.leadInteractions

    const orderCounts = leadInteractions?.value?.orderCounts

    const [filters, setFilters] = React.useState([])
    const [showAdvancedSearch, setShowAdvancedSearch] = React.useState(false)
    const [pageIndex, setPageIndex] = React.useState(0)
    const [leadsSelected, setLeadsSelected] = React.useState([])
    const [leadInfo, setLeadInfo] = React.useState(null)
    const [addressListMessage, setAddressListMessage] = React.useState(ADDRESS_LIST_MESSAGE.NONE)
    const [addressListLeadsIncluded, setAddressListLeadsIncluded] = React.useState(null)
    const [addressListLeadsExcluded, setAddressListLeadsExcluded] = React.useState(null)
    const [startDate, setStartDate] = React.useState(null)
    const [endDate, setEndDate] = React.useState(null)
    const [showingAddressCreationSuccess, setShowingAddressCreationSuccess] = React.useState(false)

    const [offices, setOffices] = React.useState([])
    const [agents, setAgents] = React.useState([])

    React.useEffect(() => {
        let timer
        if(showingAddressCreationSuccess) {
            timer = setTimeout(() => {
                setAddressListMessage(ADDRESS_LIST_MESSAGE.NONE)
                setShowingAddressCreationSuccess(false)
            }, DURATION_NOTIFICATION)
        }

        return () => clearTimeout(timer)
    }, [showingAddressCreationSuccess, DURATION_NOTIFICATION])

    const [blogs, setBlogs] = React.useState([])
    React.useEffect(() => {
        fetch('/next-api/omniSearch/search/blogs')
        .then((response) => response.json())
        .then((result) => {
            if(Array.isArray(result?.data)) {
                setBlogs(result.data)
            } else {
                setBlogs([])
            }
        })
        .catch((error) => {
            console.error(error)
        })
    }, [])

    const uniqueValues = (array, key) => {
        const unique = new Set()
        array.forEach(obj => {
            if(obj[key]) {
                const value = obj[key].toString().trim()
                if(value) {
                    unique.add(value)
                }
            }
        })
        return Array.from(unique)
    }

    const filterLists = []

    const filterOptions = [
        {
            key: 'name',
            values: uniqueValues(leadInteractionArray, 'name')
        },
        {
            key: 'city',
            values: uniqueValues(leadInteractionArray, 'city')
        },
        {
            key: 'state',
            values: uniqueValues(leadInteractionArray, 'state')
        },
        {
            key: 'zip',
            values: uniqueValues(leadInteractionArray, 'zip')
        },
        {
            key: 'orderName',
            values: uniqueValues(leadInteractionArray, 'orderName')
        },
        {
            key: 'interactionType',
            values: uniqueValues(leadInteractionArray, 'type').map(
                interactionDevKey => leadColumns.find(column => column.interactionKey === interactionDevKey)?.headerText || interactionDevKey
            )

        }
    ]

    if(userLevel < OFFICE_LEVEL) {
        React.useEffect(() => {
            fetch('/next-api/analytics/officesWithLeads')
            .then((response) => response.json())
            .then((officeList) => {
                if(Array.isArray(officeList)) {
                    setOffices(officeList)
                } else {
                    setOffices({error: 'No offices in this company have leads.'})
                }
            })
            .catch((error) => {
                console.error(error)
            })
        }, [])
    }

    if(userLevel < AGENT_LEVEL) {
        React.useEffect(() => {
            if(!idBranch.value) {
                return
            }

            fetch(`/next-api/analytics/agentsWithLeads/${idBranch.value}`)
            .then((response) => response.json())
            .then((agentList) => {
                if(Array.isArray(agentList)) {
                    setAgents(agentList)
                } else {
                    setAgents({error: 'No users in this office have leads.'})
                }
            })
            .catch((error) => {
                console.error(error)
            })

        }, [idBranch, setAgents])
    }

    filterOptions.forEach(option => {
        filterLists.push(
            {
                filterKey: option.key,
                values: option.values
            }
        )
    })

    const epochTime = (dateObj) => {
        const { year, month, day } = dateObj
        const date = new Date(year, month - 1, day)
        return date.getTime()
    }

    const cinderellaTime = (dateObj) => {
        const { year, month, day } = dateObj
        const cinderella = new Date(year, month - 1, day, 23, 59, 0, 0)
        return cinderella.getTime()
    }

    const hasInteractionInDateRange = (interactions) => {
        if(!interactions) {
            return true
        }
        if(!Array.isArray(interactions)) {
            return true
        }
        if(interactions.length < 1) {
            return true
        }

        const start = startDate ? epochTime(startDate) : 0
        const end = endDate ? cinderellaTime(endDate) : Date.now()

        for(let i = 0; i < interactions.length; i++) {
            const date = new Date(interactions[i].dateCreated?.date).getTime()
            if(date < start) {
                continue
            }
            if(date > end) {
                continue
            }
            return true
        }

        return false
    }

    const hasInteractionFromOrder = (orderNames, orderNameFilterValues) => {
        if(!orderNames || !orderNameFilterValues) {
            return true
        }
        if(!Array.isArray(orderNames) || !Array.isArray(orderNameFilterValues)) {
            return true
        }
        if(orderNames.length < 1 || orderNameFilterValues.length < 1) {
            return true
        }

        const filterValues = new Set(orderNameFilterValues)
        for(let orderName of orderNames) {
            if(filterValues.has(orderName)) {
                return true
            }
        }

        return false
    }
    const [zipCodes, setZipCodes] = React.useState(null);
    React.useEffect(() => {
        if (leadInteractions?.loading || !leadArray.length) return;

        setZipCodes(null);

        const controller = new AbortController();
        const zipList = leadArray.map(i => i.zip);
        fetch(`/next-api/analytics/getMapByZipCodes/${zipList}`, {
            signal: controller.signal
        })
            .then(res => res.json())
            .then(data => {
                if (data?.result === 'success') {
                    setZipCodes(data.zipCodes);
                } else {
                    console.warn('Failed to load zip polygons:', data);
                }
            })
            .catch(err => {
                if (err.name !== 'AbortError') {
                    console.error('Error fetching zip polygons:', err);
                }
            });
        return () => controller.abort();
    }, [leadInteractions, zipCodes?.value, idAgent]);

    const leadsFiltered = () => {
        return (
            leadArray.filter(leadInteraction => {
                if(startDate || endDate) {
                    if(!hasInteractionInDateRange(leadInteraction.interactions)) {
                        return null
                    }
                }

                const matchesFilters = filters.length === 0 || filters.every(filter => {
                    if(filter.filterKey === 'interactionType') {
                        const filterParams = filter.values
                            .map(value => leadColumns.find(column => column.headerText === value)?.interactionKey)
                            .filter(Boolean)
                        const hasQR = filterParams.includes(INTERACTION_TYPE.QR) && leadInteraction.qr > 0
                        const hasInformedDelivery = filterParams.includes(INTERACTION_TYPE.INFORMED_DELIVERY) && leadInteraction.informedDelivery > 0
                        const hasFormSubmission = filterParams.includes(INTERACTION_TYPE.FORM_SUBMISSION) && leadInteraction.formSubmission > 0
                        const hasHomeValueFormSubmission = filterParams.includes(INTERACTION_TYPE.HOME_VALUE_FORM) && leadInteraction.homeValueForm > 0

                        return hasQR || hasInformedDelivery || hasFormSubmission || hasHomeValueFormSubmission
                    }

                    if(!leadInteraction[filter.filterKey]) {
                        return null
                    }

                    if(filter.filterKey === 'orderName') {
                        return hasInteractionFromOrder(leadInteraction.orderNames, filter.values)
                    }

                    const filterValue = leadInteraction[filter.filterKey].trim()
                    return filter.values.includes(filterValue)
                })

                return matchesFilters
            })
        )
    }

    const leadInteractionsFiltered = () => {
        return (
            leadInteractionArray.filter(leadInteraction => {
                if((startDate || endDate) && !hasInteractionInDateRange(leadInteraction.interactions)) {
                    return null
                }
                const matchesFilters = filters.length === 0 || filters.every(filter => {
                    let filterParam = filter.filterKey
                    if (filter.filterKey === 'interactionType') {
                        const filterParams = filter.values
                            .map(value => leadColumns.find(column => column.headerText === value)?.interactionKey)
                            .filter(Boolean)
                        return filterParams.includes(leadInteraction.type);
                    }
                    const filterValue = leadInteraction[filterParam]
                    if(!filterValue) {
                        return null
                    }
                    return filter.values.includes(filterValue.trim())
                })

                return matchesFilters
            })
        )
    }

    const zipCodesFiltered = () => {
        if (zipCodes) {
            const leadZipsSet = new Set(leadInteractionsFiltered().map(lead => String(lead.zip)));
            const filteredZipCodes = zipCodes.filter(zip => leadZipsSet.has(String(zip.zipCode)));
            return filteredZipCodes;
        }

        return [];
    };

    const acceptLeads = async (idLeads) => {
        if(!Array.isArray(idLeads)) {
            idLeads = [idLeads]
        }

        try {
            const response = await fetch('/next-api/analytics/acceptLeads', {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idLeads: idLeads
                })
            })

            const statusBad = 400
            if(response.status < statusBad) {
                leadInteractions.reload()
            }

            return 'request completed'
        }
        catch(error) {
            console.error("Error accepting leads: ", error)
        }
    }

    const createMailingList = async (addresses) => {
        if(!addresses) {
            return
        }
        const mailingList = mailingListObject(addresses)
        if(idAgent.value) {
            const isBranchOrder = idAgent.value === ID_AGENT_BRANCH_ORDER
            const idEntity = isBranchOrder ? idBranch.value : idAgent.value
            mailingList['entityId'] = idEntity
        }
        const url = '/next-api/mailing/create_mailing_list/'
        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(mailingList)
            })
            .then(response => response.text())
            .then(data => {
                setAddressListLeadsExcluded(null)
                setAddressListLeadsIncluded(null)
                const json = JSON.parse(data)
                if(!json.result || !json.result.includes('success')) {
                    setAddressListMessage(ADDRESS_LIST_MESSAGE.ERROR)
                    return
                }
                setAddressListMessage(ADDRESS_LIST_MESSAGE.VALID)
                setShowingAddressCreationSuccess(true)
            })
        }
        catch(error) {
            setAddressListMessage(ADDRESS_LIST_MESSAGE.ERROR)
            console.error("Error creating mailing list: ", error)
        }
    }

    const hasMultipleStateLeads = uniqueValues(leadInteractionArray, 'state').length !== 1;
    return (
        <div className="analytics-dznts0">
            {
                (userLevel < AGENT_LEVEL) &&
                <div className="elevated-user">
                    {
                        (userLevel < OFFICE_LEVEL && !offices?.error) &&
                        SelectiveOfficeSelect({offices, idBranch, idAgent, setFilters, isInternalUser})
                    }
                    {
                        (userLevel < AGENT_LEVEL && !offices?.error && !agents?.error) &&
                        SelectiveAgentSelect({agents, idAgent, setLeadsSelected, setFilters, isInternalUser})
                    }
                </div>
            }
            <div className="leads-container">
                {
                    leadsEmpty && (<>
                        <div className="analytics-empty-overlay">
                            <div className="analytics-empty-container">
                                <h5 className="analytics-empty-header">
                                    {offices?.error || agents?.error || 'Looking to track leads?'}
                                </h5>
                                <p className="analytics-empty-message">
                                    Place a mailed order with a QR code or &nbsp;
                                    <a
                                        href="https://www.xpressdocs.com/s/informed-delivery-service/"
                                        target="_blank"
                                        className="informed-delivery"
                                        {...xdQAAttribute('link', 'informed delivery')}
                                    >
                                        Informed Delivery
                                    </a>
                                </p>
                                <p className="analytics-empty-message">
                                    to begin tracking your campaigns' success.
                                </p>
                                <button
                                    onClick={
                                        async () => {
                                            sessionStorage.setItem("mailingOnly", "true")
                                            routerHistory.push("/")
                                            scrollTo(0, 0)
                                        }}
                                    {...xdQAAttribute('button', 'Start an Order')}
                                >Start an Order</button>
                                <div className="blog">
                                    <AnalyticsBlog {...{blogs, settings, emptyState: true}}/>
                                </div>
                            </div>
                        </div>
                    </>)
                }
                {!leadsEmpty && <AnalyticsOverview
                    {...{leadInteractions: leadInteractionArray, orderCounts}}
                />}
                {!leadsEmpty && <AnalyticsFilters
                    {...{
                        filterLists,
                        setPageIndex,
                        filters, setFilters,
                        startDate, setStartDate,
                        endDate, setEndDate,
                        showAdvancedSearch, setShowAdvancedSearch
                    }}
                />}
                {(!leadsEmpty && leadCount < (settings?.hideBlogsThreshold ?? 15)) && (
                    <AnalyticsBlog {...{blogs, settings}}/>
                )}
                <AnalyticsLocale
                    leadInteractions={leadInteractionsFiltered()}
                    unfilteredLeadInteractions={leadInteractionArray}
                    zipCodes={zipCodesFiltered()}
                    {...{filters, setFilters, settings, blogs, routerHistory, userLevels, hasMultipleStateLeads, orderCounts}}
                />
                {(!leadsEmpty && leadCount >= (settings?.hideBlogsThreshold ?? 15)) && (
                    <AnalyticsBlog {...{blogs, settings}}/>
                )}
                <AnalyticsLeads
                    leadInteractions={leadsFiltered()}
                    {...{
                        acceptLeads,
                        setShowAdvancedSearch,
                        setLeadInfo,
                        createMailingList,
                        addressListMessage, setAddressListMessage,
                        addressListLeadsExcluded, setAddressListLeadsExcluded,
                        addressListLeadsIncluded, setAddressListLeadsIncluded,
                        filters, setFilters,
                        leadsSelected, setLeadsSelected,
                        startDate, setStartDate,
                        endDate, setEndDate,
                        pageIndex, setPageIndex
                    }}
                />
                <AnalyticsLeadInfo
                    {...{
                        leadInfo, setLeadInfo
                    }}
                />
                <AnalyticsAddressListModal
                    {...{
                        addressListMessage, setAddressListMessage,
                        addressListLeadsExcluded, setAddressListLeadsExcluded,
                        addressListLeadsIncluded, setAddressListLeadsIncluded,
                        createMailingList
                    }}
                />
            </div>
        </div>
    )
}

const INTERACTION_TYPE = {
    QR: 'qr',
    INFORMED_DELIVERY: 'informedDelivery',
    FORM_SUBMISSION: 'formSubmission',
    HOME_VALUE_FORM: 'homeValueForm'
}

const mailingListObject = (data) => {
    return {
        data,
        'listingType': 'upload',
        "alternateName": {"type": "owner"},
        "listName": `SelectedLeads-${currentDate()}`,
        "countryCode": "US"
    }
}

const currentDate = (timeZone = 'America/Chicago') => {
    const now = new Date()
    const options = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }
    const [month, day, year] = now.toLocaleDateString('en-US', options).split('/')
    return `${year}-${month}-${day}`
}

export const xdQAAttribute = (type='text', name='yourNameHere') => {
    return {
        'xd-qa-type': type,
        'xd-qa-name': name
    }
}